import React from 'react'
import clsx from 'clsx'
import { format as formatDate } from 'date-fns'
import { useParams, useHistory } from 'react-router'
import { Snackbar } from '@mui/material'
import { Alert } from '@mui/material'
import { Clear, Create, Crop } from '@mui/icons-material'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'
import { MessageText1 } from 'iconsax-react'
import { useTheme } from '@mui/material'
import MediaAvatar from '../../../atoms/MediaAvatar'
import StatusMenu from '../../../molecules/StatusMenu'
import TypesMenu from '../../../molecules/TypesMenu'
import TimezonesMenu from '../../../molecules/TimezonesMenu'
import ErrorToast from '../../../molecules/ErrorToast'
import CropImage from '../../../molecules/CropImage'
import Button from '@SonarCX/components/atoms/Button2'
import UserPanelAccordion from '../../../molecules/UserPanelAccordion'
import ChannelSection from '../../../molecules/ChatModals/AddCustomerModal/ChannelSection'
import UserDetails from '../../UserProfile/UserDetails'
import PortalAccess from '@SonarCX/components/molecules/ChatModals/AddCustomerModal/PortalAccess'

import { Routes } from '@SonarCX/Routes'
import { useCustomerContext } from '@SonarCX/providers/CustomerProvider'
import { useEditContext } from '@SonarCX/providers/EditProvider'
import { createViewableSession } from '../../../molecules/BaseConversation/RecentActivity/helper'
import {
  useSocket,
  CONVERSATION_FILTERS
} from '@SonarCX/providers/SocketProvider'
import { useEnvironment } from '@SonarCX/providers/EnvironmentProvider'
import { useBreakpoint } from '@SonarCX/providers/BreakpointProvider'
import { useAreYouSure } from '@SonarCX/providers/AreYouSureProvider'
import {
  convertToTimezone,
  formatDateRelative,
  getReasonWarningText,
  getShortCode
} from '@SonarCX/utilities'
import { MAX_FILESIZE } from '@SonarCX/utilities/constants'
import { fetchTypes, selectUserType } from '@SonarCX/slices/userType'
import { useUserAPI } from '@SonarCX/providers/UserAPIProvider'
import { AddSuccessAlert } from '@SonarCX/slices/alert'
import { selectTimezone } from '@SonarCX/slices/timezone'

import useInterval from '@SonarCX/hooks/useInterval'
import useUserPermissions from '@SonarCX/hooks/permissions/useUserPermissions'

import useStyles from './styles'

interface DetailsPaneProps {
  user: LocalUser
  setImg: CallableFunction
  onStatusChange: CallableFunction
  onTypeChange: CallableFunction
  onTimezoneChange: (x: string) => void
  isTeam?: boolean
  canEdit: boolean
  update?: () => void
  updateUser?: (user: any) => void
  setDetails?: (details: any) => void
}

const DetailsPane: React.FC<DetailsPaneProps> = ({
  user,
  setImg,
  onStatusChange,
  onTypeChange,
  onTimezoneChange,
  isTeam,
  canEdit,
  update,
  updateUser,
  setDetails
}) => {
  // Gets props from ContactDetails
  const history = useHistory()
  const dispatch = useAppDispatch()
  const { id } = useParams() as Record<string, string>
  const theme = useTheme()

  const {
    types,
    loaded: userTypesLoaded,
    loading: userTypesLoading
  } = useAppSelector(selectUserType)
  const { current } = useAppSelector(selectTimezone)
  const { customerSession } = useCustomerContext()
  const { setEditedImg } = useEditContext()
  const { user: currentUser, update: updateEnvUser } = useEnvironment()
  const { uploadUser } = useUserAPI()
  const { isSmall } = useBreakpoint()
  const {
    api: { handleFilterChange }
  } = useSocket()
  const { checkCanSetInternal, checkCanSetExternal } = useUserPermissions()

  const { setAction, setReason, setSubtext, setTitle, setV2 } = useAreYouSure()
  const [uploadImg, setUploadImg] = React.useState(null)
  const [originalImg, setOriginalImg] = React.useState(null)
  const [message, setMessage] = React.useState<any[] | string>([])
  const [error, setError] = React.useState(null)
  const [name, setName] = React.useState(user?.name)
  const [currentTime, setCurrentTime] = React.useState(new Date())
  const [showCrop, setShowCrop] = React.useState(false)
  const [cropping, setCropping] = React.useState(false)

  useInterval(100, () => {
    setCurrentTime(new Date())
  })

  const canSetInternal = React.useMemo(
    () => checkCanSetInternal(isTeam, canEdit),
    [checkCanSetInternal, isTeam, canEdit]
  )

  const canSetExternal = React.useMemo(
    () => checkCanSetExternal(isTeam, canEdit, user),
    [checkCanSetExternal, isTeam, canEdit, user]
  )

  const classes = useStyles({
    canEdit,
    disabled: !canEdit || (!canSetInternal && !canSetExternal),
    smallMedia: isSmall
  })

  const handleImgUpload = async e => {
    let file

    if (e.target.files.length !== 0) {
      file = e.target.files[0]

      // checking file size before uploading
      if (file.size > MAX_FILESIZE) {
        setError({
          err: {
            message: `${file.name} is too large -- files must be smaller than 100MB.`
          }
        })
      } else {
        // upload image to avatar area
        const url = URL.createObjectURL(file)
        setUploadImg(url)
        setOriginalImg(url)
        setImg(file)
        setEditedImg(true)
      }
    }
  }

  // closing alert snackbar
  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return
    }
    setMessage('')
    setError(null)
  }

  const goToTimeline = async () => {
    if (customerSession) {
      handleFilterChange(CONVERSATION_FILTERS.OPEN, false)
      history.push(`${Routes.TIMELINE}/?session=${customerSession}`)
    } else {
      // no recent activities on the user
      const newSessionID = await createViewableSession(id)
      history.push(`${Routes.TIMELINE}/?session=${newSessionID}`)
    }
  }

  const userTypeChange = React.useCallback(
    val => {
      if (user?.type_id === val) return

      const newType = types.find(type => type.id === val)
      const oldType = types.find(type => type.id === user?.type_id)

      if (newType?.external === oldType?.external) {
        onTypeChange(val)
        return
      }

      setTitle('ARE YOU SURE?')
      const reasonTxt = getReasonWarningText(newType, oldType)
      setReason(reasonTxt)
      setSubtext('')
      setAction(() => onTypeChange(val))
      setV2(true)
    },
    [
      onTypeChange,
      setTitle,
      setAction,
      setReason,
      setSubtext,
      setV2,
      types,
      user.type_id
    ]
  )

  const handleNameChangeBlur = React.useCallback(async () => {
    if (!name) {
      setName(user?.name)
    } else {
      const toSave = { ...user, name }
      uploadUser(toSave)
      if (user?.id === currentUser?.id) {
        updateEnvUser(toSave)
      }
      dispatch(AddSuccessAlert('Changes saved', 500))
    }
  }, [name, user, uploadUser, currentUser, updateEnvUser, dispatch])

  React.useEffect(() => {
    if (!userTypesLoaded && !userTypesLoading) {
      dispatch(fetchTypes())
    }
  }, [userTypesLoaded, userTypesLoading, dispatch])

  //Update name when merge contact
  React.useEffect(() => {
    if (user?.name) {
      setName(user.name)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id])

  const ImageInput = (
    <input
      type='file'
      hidden
      onChange={e => {
        handleImgUpload(e)
        setShowCrop(true)
      }}
      disabled={!canEdit}
    />
  )

  const ClearIcon = (
    <Clear
      onClick={e => {
        e.preventDefault()
        e.stopPropagation()

        setAction(() => {
          setUploadImg(null)
          setOriginalImg(null)
          setImg('REMOVE')
          setEditedImg(true)
        })
        setReason('Are you sure you want to remove this avatar?')
        setSubtext('')
        setTitle('ARE YOU SURE?')
        setV2(true)
      }}
      className={classes.clearIcon}
    />
  )

  return (
    <div className={classes.root}>
      {uploadImg ? (
        // uploaded an image
        <label className={classes.title}>
          {ImageInput}

          <div
            className={classes.avatar}
            style={{
              position: 'relative',
              backgroundImage: `url(${uploadImg})`,
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              borderRadius: '100%'
            }}
          />

          {canEdit && showCrop && (
            <>
              <Crop
                onClick={e => {
                  e.preventDefault()
                  e.stopPropagation()
                  setCropping(true)
                }}
                className={classes.uploadIcon}
              />

              {cropping && (
                <CropImage
                  imgSrc={originalImg}
                  onClose={() => setCropping(false)}
                  onCrop={file => {
                    setUploadImg(URL.createObjectURL(file))
                    setImg(file)
                  }}
                />
              )}
            </>
          )}

          {ClearIcon}
        </label>
      ) : user?.avatar_id ? (
        // has existing image
        <label className={classes.title}>
          <div></div>

          {ImageInput}

          <MediaAvatar
            user={user}
            hideCard
            className={classes.avatar}
            style={{
              position: 'relative',
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              borderRadius: '100%',
              backgroundImage: `url(${uploadImg})`
            }}
          />

          {ClearIcon}
        </label>
      ) : (
        // need to upload an image
        <label className={classes.title}>
          {ImageInput}

          <MediaAvatar
            user={user}
            hideCard
            className={classes.avatar}
            style={{
              position: 'relative',
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              borderRadius: '100%'
            }}
          />

          <Create className={classes.uploadIcon} />
        </label>
      )}

      <div className={classes.name}>
        <input
          value={name}
          className={classes.nameInput}
          onChange={e => setName(e.target.value)}
          onBlur={handleNameChangeBlur}
        />
        {user?.deleted_at && <div>(Deleted)</div>}
        <div className={classes.time}>
          Local time:{' '}
          {formatDate(
            convertToTimezone(user.timezone, currentTime, current),
            'h:mm a'
          )}{' '}
          {getShortCode(user.timezone, current)}
        </div>
      </div>

      <div className={classes.fields}>
        <UserPanelAccordion title='About'>
          <div className={classes.fieldsContainer}>
            {!isTeam && (
              <div className={classes.field}>
                <div style={{ marginBottom: '1rem' }}>
                  <div className={classes.fieldName}>STATUS</div>

                  <StatusMenu
                    id='user-status'
                    selected={user?.status_id}
                    onSelect={onStatusChange}
                    className={clsx(classes.fieldValue, classes.status)}
                    disabled={!canEdit}
                  />
                </div>
              </div>
            )}

            <div style={{ marginBottom: '1rem' }}>
              <div className={classes.fieldName}>TYPE</div>

              <TypesMenu
                id='user-type'
                selected={user?.type_id}
                onSelect={userTypeChange}
                className={clsx(classes.fieldValue, classes.type)}
                disabled={!canEdit || (!canSetInternal && !canSetExternal)}
                hideInternal={!canSetInternal}
                hideExternal={!canSetExternal}
              />
            </div>

            <div>
              <div className={classes.fieldName}>TIMEZONE</div>

              <TimezonesMenu
                id='user-timezone'
                selected={user?.timezone}
                onSelect={onTimezoneChange}
                className={clsx(classes.fieldValue, classes.status)}
                disabled={!canEdit}
              />
            </div>

            <div className={classes.field}>
              <div className={classes.fieldName}>CREATED</div>

              <div className={clsx(classes.fieldValue, classes.created)}>
                {formatDateRelative(user?.created_at || new Date(), true)}
              </div>
            </div>

            {isTeam && (
              <div className={classes.field} data-testid='user-details-info'>
                <UserDetails
                  details={user}
                  update={update}
                  updateUser={updateUser}
                  setDetails={setDetails}
                  canEdit={canEdit}
                />
              </div>
            )}
          </div>
        </UserPanelAccordion>

        <UserPanelAccordion title='Communications'>
          <ChannelSection user={user} canEdit={canEdit} />
        </UserPanelAccordion>

        {!isTeam && (
          <UserPanelAccordion title='Portal Access'>
            <PortalAccess user={user} canEdit={canEdit} />
          </UserPanelAccordion>
        )}
      </div>

      {!isTeam && !isSmall && (
        <div className={classes.goto} onClick={() => goToTimeline()}>
          <Button
            className={classes.gotoBtn}
            startIcon={<MessageText1 size={theme.size.base} variant='Bulk' />}
          >
            GO TO TIMELINE
          </Button>
        </div>
      )}

      <Snackbar
        open={message.length > 0}
        autoHideDuration={3000}
        onClose={handleClose}
      >
        <Alert
          //@ts-ignore
          onClose={handleClose}
          severity={'success'}
          variant='filled'
          elevation={0}
        >
          {message}
        </Alert>
      </Snackbar>
      <ErrorToast error={error} />
    </div>
  )
}

export default DetailsPane
